import React, { useState } from 'react';

interface Leader {
  id: number;
  name: string;
  position: string;
  image: string;
  bio: string;
  achievements: string[];
  skills: string[];
}

const LeaderFlipCards: React.FC = () => {
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());

  // Datos de ejemplo de líderes
  const leaders: Leader[] = [
    {
      id: 1,
      name: "María González",
      position: "CEO & Founder",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b647?w=300&h=300&fit=crop&crop=face",
      bio: "Líder visionaria con más de 15 años de experiencia en transformación digital y desarrollo de equipos de alto rendimiento.",
      achievements: ["Incrementó ingresos 300%", "Expandió a 12 países", "Premio Líder del Año 2023"],
      skills: ["Estrategia", "Liderazgo", "Innovación"]
    },
    {
      id: 2,
      name: "Carlos Rodriguez",
      position: "CTO",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      bio: "Experto en tecnología con pasión por la innovación y el desarrollo de productos que impactan positivamente la sociedad.",
      achievements: ["Lanzó 50+ productos", "Patentes internacionales", "Speaker en TechConf 2024"],
      skills: ["Desarrollo", "AI/ML", "Cloud Computing"]
    },
    {
      id: 3,
      name: "Ana Martínez",
      position: "Head of Operations",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face",
      bio: "Especialista en optimización de procesos con un enfoque data-driven para maximizar la eficiencia operacional.",
      achievements: ["Redujo costos 40%", "Implementó ISO 9001", "Certificación Lean Six Sigma"],
      skills: ["Operaciones", "Analytics", "Process Improvement"]
    },
    {
      id: 4,
      name: "Diego Fernández",
      position: "Head of Sales",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      bio: "Estratega comercial con un historial probado en crecimiento de ventas y construcción de relaciones duraderas con clientes.",
      achievements: ["Superó metas 150%", "Team of the Year 2023", "Nuevos mercados LATAM"],
      skills: ["Ventas", "Negociación", "CRM"]
    }
  ];

  const handleCardFlip = (id: number) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: 'var(--bg-main)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" style={{ color: 'var(--blue)' }}>
            Nuestros Líderes
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--gray-dark)' }}>
            Conoce al equipo excepcional que impulsa nuestra visión hacia el futuro
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className="group perspective-1000 h-96"
              onClick={() => handleCardFlip(leader.id)}
            >
              <div
                className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                  flippedCards.has(leader.id) ? 'rotate-y-180' : ''
                }`}
              >
                {/* Front of card */}
                <div className="absolute inset-0 w-full h-full backface-hidden rounded-2xl shadow-2xl" style={{ 
                  backgroundColor: 'var(--white)', 
                  border: '2px solid var(--blue-accent)',
                  boxShadow: '0 10px 30px rgba(46, 138, 188, 0.15)'
                }}>
                  <div className="p-6 h-full flex flex-col items-center justify-center text-center">
                    <div className="relative mb-6">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="w-24 h-24 rounded-full object-cover shadow-lg image-hover-effect"
                        style={{ border: '4px solid var(--blue-accent)' }}
                      />
                      <div className="absolute inset-0 rounded-full" style={{ 
                        background: 'linear-gradient(135deg, rgba(46, 138, 188, 0.1), rgba(156, 200, 223, 0.1))' 
                      }}></div>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--blue)' }}>
                      {leader.name}
                    </h3>
                    
                    <p className="font-medium mb-4" style={{ color: 'var(--gray-medium)' }}>
                      {leader.position}
                    </p>
                    
                    <div className="mt-auto">
                      <div className="button inline-flex items-center">
                        <span className="text-sm font-medium mr-2">
                          Ver más
                        </span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--white)' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back of card */}
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-2xl shadow-2xl" style={{ 
                  backgroundColor: 'var(--blue)', 
                  border: '2px solid var(--blue-light)',
                  boxShadow: '0 10px 30px rgba(46, 138, 188, 0.3)'
                }}>
                  <div className="p-6 h-full flex flex-col">
                    <div className="flex items-center mb-4">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="w-12 h-12 rounded-full object-cover mr-3"
                        style={{ border: '2px solid var(--white)' }}
                      />
                      <div>
                        <h3 className="font-bold text-lg" style={{ color: 'var(--white)' }}>{leader.name}</h3>
                        <p className="text-sm" style={{ color: 'var(--blue-accent)' }}>{leader.position}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--white)' }}>
                      {leader.bio}
                    </p>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2 text-sm" style={{ color: 'var(--green-accent)' }}>Logros Clave:</h4>
                      <ul className="space-y-1">
                        {leader.achievements.map((achievement, index) => (
                          <li key={index} className="text-xs flex items-start" style={{ color: 'var(--white)' }}>
                            <span className="mr-2" style={{ color: 'var(--green-accent)' }}>•</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mt-auto">
                      <h4 className="font-semibold mb-2 text-sm" style={{ color: 'var(--green-accent)' }}>Especialidades:</h4>
                      <div className="flex flex-wrap gap-1">
                        {leader.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: 'var(--green-accent)',
                              color: 'var(--gray-dark)',
                              border: '1px solid var(--white)'
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-sm" style={{ color: 'var(--gray-medium)' }}>
            Haz clic en cualquier tarjeta para conocer más detalles sobre nuestros líderes
          </p>
        </div>
      </div>

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        
        /* Aplicar fuente Inter */
        * {
          font-family: 'Inter', sans-serif;
        }
        
        /* Efecto hover mejorado para las cards */
        .group:hover .image-hover-effect {
          transform: scale(1.05);
        }
        
        /* Transición suave para el botón */
        .button {
          transition: all 0.3s ease;
        }
        
        .button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(46, 138, 188, 0.3);
        }
      `}</style>
    </div>
  );
};

export default LeaderFlipCards;